<script setup lang="ts">
type SocialModalEmits = {
  close: []
}

interface SocialModalProps {
  modalTitle: string
  modalDescription: string
  socialsTitle: string
  socialsLink: string
}

const emits = defineEmits<SocialModalEmits>()
const props = defineProps<SocialModalProps>()

const isOpen = defineModel<boolean>('open')

const { t } = useI18n('components.templates.social.social-modal')

const hasCopied = ref(false)

const copyLink = async () => {
  hasCopied.value = await copyStringToClipBoard(props.socialsLink)
}
</script>

<template>
  <VModal
    v-model:open="isOpen"
    cancel-label="buttons.close"
    :title="modalTitle"
    :description="modalDescription"
    @close="emits('close')"
  >
    <template #content>
      <div class="columns multiline">
        <div
          class="column flex! cursor-pointer items-center text-black half"
          @click="copyLink"
        >
          <span class="social-share-button w-full">
            <VIcon
              class="min-w-12"
              :icon="hasCopied ? 'fa-check' : 'fa-copy'"
              size="large"
            />
            <span class="social-share-button__label">
              <span class="ml-2 text-left">
                {{
                  hasCopied
                    ? t('.labels.copy-link-success')
                    : t('.labels.copy-link-initial')
                }}
              </span>
            </span>
          </span>
        </div>

        <div class="column flex! items-center half">
          <SocialShare
            network="facebook"
            :styled="false"
            :title="socialsTitle"
            :url="socialsLink"
          >
            <template #icon>
              <VIcon
                class="min-w-12"
                icon="fa-facebook"
                icon-pack="fa-brands"
                size="large"
                color="black"
              />
            </template>
            <template #label>
              <span class="ml-2 w-full text-left text-black">
                {{ t('.labels.facebook') }}
              </span>
            </template>
          </SocialShare>
        </div>

        <div class="column flex! items-center half">
          <SocialShare
            class="text-black"
            network="whatsapp"
            :styled="false"
            :title="socialsTitle"
            :url="socialsLink"
          >
            <template #icon>
              <VIcon
                class="min-w-12"
                icon="fa-whatsapp"
                icon-pack="fa-brands"
                size="large"
                color="black"
              />
            </template>
            <template #label>
              <span class="ml-2 w-full text-left text-black">
                {{ t('.labels.whatsapp') }}
              </span>
            </template>
          </SocialShare>
        </div>

        <div class="column flex! items-center half">
          <SocialShare
            network="twitter"
            :styled="false"
            :title="socialsTitle"
            :url="socialsLink"
          >
            <template #icon>
              <VIcon
                class="min-w-12"
                icon="fa-x-twitter"
                icon-pack="fa-brands"
                size="large"
                color="black"
              />
            </template>
            <template #label>
              <span class="ml-2 w-full text-left text-black">
                {{ t('.labels.x') }}
              </span>
            </template>
          </SocialShare>
        </div>

        <div class="column flex! items-center half">
          <SocialShare
            network="linkedin"
            :styled="false"
            :title="socialsTitle"
            :url="socialsLink"
          >
            <template #icon>
              <VIcon
                class="min-w-12"
                icon="fa-linkedin"
                icon-pack="fa-brands"
                size="large"
                color="black"
              />
            </template>
            <template #label>
              <span class="ml-2 w-full text-left text-black">
                {{ t('.labels.linkedin') }}
              </span>
            </template>
          </SocialShare>
        </div>

        <div class="column flex! items-center half">
          <SocialShare
            network="email"
            :styled="false"
            :title="socialsTitle"
            :url="socialsLink"
          >
            <template #icon>
              <VIcon class="min-w-12" icon="fa-envelope" size="large" color="black" />
            </template>
            <template #label>
              <span class="ml-2 w-full text-left text-black">
                {{ t('.labels.email') }}
              </span>
            </template>
          </SocialShare>
        </div>
      </div>
    </template>
  </VModal>
</template>

<style scoped>
.social-share-button__label {
  min-width: 8rem;
}
</style>
